import {IconButton, Link} from "@mui/material";
import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {FiTrash2} from "react-icons/fi";
import apiClient from "../../utils/apiClient";
import {getApiErrorMessage} from "../../utils/apiError";
import {snackbar} from "../../utils/snackbar";
import useApiRequest from "../../hooks/useApiRequest";
import useConfirmDialog from "../../hooks/useConfirmDialog";
import ConfirmDialog from "../ui/ConfirmDialog";
import ListView from "../ListView/ListView";
import TenantUserRolesFilterBar from "../Tenants/TenantUserRolesFilterBar";

const normalizeArray = (value) => {
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value)) return value;
  return [];
};

const UserTenantsGrid = ({userId, readOnly = false}) => {
  const navigate = useNavigate();
  const [userTenantRoles, setUserTenantRoles] = useState([]);

  const {loading, list: listUserTenantRoles} = useApiRequest({
    baseUrl: "/tenant-user-roles",
    resource: "",
    field: ""
  });

  const {confirmOpen, itemName, openDialog, handleConfirm, handleCancel} = useConfirmDialog(async (rowId) => {
    const found = userTenantRoles.find((r) => `${r.tenant_id}-${r.role_id}` === rowId);
    if (!found) return;
    try {
      await apiClient.delete(
        `/tenants/${found.tenant_id}/users/${found.user_id}/roles/${encodeURIComponent(found.role_id)}`
      );
      setUserTenantRoles((prev) => prev.filter((r) => `${r.tenant_id}-${r.role_id}` !== rowId));
      snackbar.success("Rol del tenant eliminado correctamente.");
    } catch (error) {
      snackbar.error(getApiErrorMessage(error));
    }
  });

  const handleSearch = async (params) => {
    const result = await listUserTenantRoles(params);
    if (result?.success) {
      setUserTenantRoles(normalizeArray(result.data));
    }
  };

  const columns = [
    {
      field: "tenant_name",
      headerName: "Tenant",
      flex: 1,
      renderCell: (params) => (
        <Link
          component="button"
          underline="hover"
          onClick={() => navigate("/tenant-form", {state: {id: params.row.tenant_id}})}
        >
          {params.value || params.row.tenant_id}
        </Link>
      )
    },
    {
      field: "role_name",
      headerName: "Rol",
      flex: 1,
      renderCell: (params) => (
        <Link
          component="button"
          underline="hover"
          onClick={() => navigate("/role-form", {state: {id: params.row.role_id}})}
        >
          {params.value || params.row.role_id}
        </Link>
      )
    },
    {
      field: "product_name",
      headerName: "Producto",
      flex: 1,
      renderCell: (params) =>
        params.value ? (
          <Link
            component="button"
            underline="hover"
            onClick={() => navigate("/product-form", {state: {id: params.row.product_id}})}
          >
            {params.value}
          </Link>
        ) : (
          "-"
        )
    },
    {
      field: "assigned_at",
      headerName: "Asignado en",
      flex: 1,
      renderCell: (params) => (params.value ? new Date(params.value).toLocaleString() : "-")
    },
    ...(readOnly
      ? []
      : [
          {
            field: "actions",
            headerName: "Acciones",
            width: 150,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
              <IconButton
                aria-label="delete"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  openDialog(
                    `${params.row.tenant_id}-${params.row.role_id}`,
                    `${params.row.tenant_name} - ${params.row.role_name}`
                  );
                }}
              >
                <FiTrash2 />
              </IconButton>
            )
          }
        ])
  ];

  return (
    <>
      <TenantUserRolesFilterBar userId={userId} onSearch={handleSearch} />
      <ListView
        columns={columns}
        data={userTenantRoles}
        getRowId={(row) => `${row.tenant_id}-${row.role_id}`}
        loading={loading}
      />
      <ConfirmDialog
        open={confirmOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        entityName="relación tenant-rol"
        entityLabel={itemName}
      />
    </>
  );
};

export default UserTenantsGrid;
